import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import config from "../config";
import { jwtDecode, JwtPayload } from "jwt-decode"; 
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: {
    user_name: string;
    user_email: string;
    user_type: string;
  } | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

interface CustomJwtPayload extends JwtPayload {
  user_name: string;
  user_email: string;
  user_type: string;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthContextType["user"]>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Função para validar o token 
  const validateToken = async (accessToken: string) => {
    try {
      const response = await axios.post(`${config.apiUrl}/validate`, { token: accessToken });
      return response.status === 200;
    } catch {
      return false;
    }
  };

  // Função para renovar o token
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken"); 
      if (!refreshToken) return false;

      const response = await axios.post(`${config.apiUrl}/refresh`, { refreshToken });

      if (response.status === 200) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem("token", accessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        setToken(accessToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

        return true;
      }
    } catch (error) {
      console.error("Erro ao renovar o token:", error);
    }
    return false;
  };

  // Função para tentar restaurar a sessão ao carregar a página
  const restoreSession = async () => {
    const storedToken = localStorage.getItem("token");

    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode<CustomJwtPayload>(storedToken);
      const currentTime = Date.now() / 1000;

      if (decoded.exp && decoded.exp < currentTime) {
        console.warn("Token expirado, tentando renovar...");
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          logout();
        }
      } else {
        const isValid = await validateToken(storedToken);
        if (isValid) {
          setUser(decoded);
          setToken(storedToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
        } else {
          console.warn("Token inválido, tentando renovar...");
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            logout();
          }
        }
      }
    } catch (error) {
      console.error("Erro ao validar token:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Executa a verificação ao carregar a página
  useEffect(() => {
    restoreSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await axios.post(`${config.apiUrl}/login`, { email, password }, { withCredentials: true });

      const { accessToken, refreshToken } = response.data;
      const decoded = jwtDecode<CustomJwtPayload>(accessToken);

      if (decoded.user_type !== "USER") {
        throw new Error("Usuário não autorizado.");
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      setUser(decoded);
      setToken(accessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    } catch (error: any) {
      console.error("Erro ao fazer login:", error);
      throw new Error("Erro ao fazer login. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};
